if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.pendingRequestUrl = null;
    }

    connectedCallback() {
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.updateSelectionMetadata();
    }
    
    onClick(event) {
      if (event.target.closest('.variant-picker__size-guide-btn')) {
        this.onSizeGuideClick(event);
        return;
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    onVariantChange(event) {
      const target = this.getInputForEventTarget(event.target);
      if (!target) return;

      this.updateSelectionMetadata(event);
      
      const selectedOptionValues = this.getSelectedOptionValues();
      const productUrl = target.dataset.productUrl || this.dataset.productUrl;
      
      this.publishVariantChange({
        event,
        target,
        selectedOptionValues,
        productUrl
      });

      this.fetchUpdatedProductInfo(productUrl, selectedOptionValues, target.id);
    }

    updateSelectionMetadata(event = null) {
      if (!event) return;

      const { target } = event;
      const { value, tagName } = target;

      if (tagName === 'SELECT' && target.selectedOptions.length) {
        Array.from(target.options)
          .find((option) => option.getAttribute('selected'))
          ?.removeAttribute('selected');
        target.selectedOptions[0].setAttribute('selected', 'selected');

        const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
        const swatchElement = target
          .closest('.variant-picker__dropdown')
          ?.querySelector('.variant-picker__dropdown-swatch .swatch');
        
        if (swatchElement && swatchValue) {
          swatchElement.style.setProperty('--swatch--background', swatchValue);
          swatchElement.classList.remove('swatch--unavailable');
          
          const focalPoint = target.selectedOptions[0].dataset.optionSwatchFocalPoint;
          if (focalPoint) {
            swatchElement.style.setProperty('--swatch-focal-point', focalPoint);
          }
        } else if (swatchElement) {
          swatchElement.style.setProperty('--swatch--background', 'unset');
          swatchElement.classList.add('swatch--unavailable');
        }
      }

      if (tagName === 'INPUT' && target.type === 'radio') {
        const selectedValueElement = target
          .closest('.variant-picker__fieldset')
          ?.querySelector('[data-selected-value]');
        
        if (selectedValueElement) {
          selectedValueElement.textContent = value;
        }
      }
    }

    fetchUpdatedProductInfo(productUrl, selectedOptionValues, targetId) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const requestUrl = this.buildRequestUrl(productUrl, selectedOptionValues);
      this.pendingRequestUrl = requestUrl;

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          
          
          this.updateProductInfo(html);
          this.updateURL(productUrl, this.getSelectedVariant(html));
          
          document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
          } else {
            console.error('[Variant Picker] Fetch error:', error);
          }
        });
    }

      updateProductInfo(html) {
        const variant = this.getSelectedVariant(html);

        this.updateVariantPicker(html);

        this.publishVariantSelected(variant, html);

        if (!variant) {
          this.setUnavailable();
          return;
        }

      const sectionId = this.dataset.section;
      
      if (document.getElementById(`price-${sectionId}`)) {
        this.updateElement(html, 'price');
      }
      if (document.getElementById(`price-info-${sectionId}`)) {
        this.updateElement(html, 'price-info');
      }
      if (document.getElementById(`Sku-${sectionId}`)) {
        this.updateElement(html, 'Sku');
      }
      if (document.getElementById(`Inventory-${sectionId}`)) {
        this.updateElement(html, 'Inventory');
      }
      
      if (document.getElementById(`Volume-${sectionId}`)) {
        this.updateElement(html, 'Volume', true);
      }
      if (document.getElementById(`Price-Per-Item-${sectionId}`)) {
        this.updateElement(html, 'Price-Per-Item', true);
      }
      if (document.getElementById(`Volume-Note-${sectionId}`)) {
        this.updateElement(html, 'Volume-Note', true);
      }
      
      this.setAvailable();
      
      this.updateAddToCartButton(html, variant);
      this.updateQuantityRulesCart(html, variant);
      
      this.updateVariantPicker(html);
    }

    updateVariantPicker(html) {
      const newPicker = html.querySelector('variant-picker');
      if (!newPicker) return;

      const activeElement = document.activeElement;

      this.innerHTML = newPicker.innerHTML;
      
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      
      if (activeElement && activeElement.closest('variant-picker')) {
        const newActiveElement = this.querySelector(`[name="${activeElement.name}"]`);
        if (newActiveElement) {
          newActiveElement.focus({ preventScroll: true });
        }
      }
    }

    updateElement(html, elementId, isOptional = false) {
      const sectionId = this.dataset.section;
      const fullId = `${elementId}-${sectionId}`;
      const source = html.getElementById(fullId);
      const destination = document.getElementById(fullId);
      
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('hidden', source.classList.contains('hidden') || !source.innerText.trim());
      } else if (!isOptional) {
        console.warn(`[Variant Picker] ✗ Could not update ${elementId}`, {
          fullId: fullId,
          sourceHTML: source ? 'Found' : 'Not found',
          destinationHTML: destination ? 'Found' : 'Not found'
        });
      }
    }

    updateAddToCartButton(html, variant) {
      const sectionId = this.dataset.section;
      const container = this.getProductFormContainer();
      const currentForm = this.getProductForm(container);
      const currentButton = currentForm?.querySelector('.product-form__submit')
        || container?.querySelector('.product-form__submit');

      if (!currentButton) return;

      const newButton = (sectionId && html.querySelector(`#product-form-${sectionId} .product-form__submit`))
        || html.querySelector('.product-form__submit');

      if (!newButton) return;

      const isDisabled = newButton.hasAttribute('disabled');
      const newButtonText = newButton.querySelector('.button__text')?.textContent;

      if (isDisabled) {
        currentButton.disabled = true;
        currentButton.setAttribute('aria-disabled', 'true');
      } else {
        currentButton.disabled = false;
        currentButton.removeAttribute('aria-disabled');
      }

      const textSpan = currentButton.querySelector('.button__text');
      if (textSpan && newButtonText) {
        textSpan.textContent = newButtonText;
      }

      const variantIdInput = currentForm?.querySelector('input[name="id"]')
        || container?.querySelector('input[name="id"]');
      if (variantIdInput) {
        if (variant && variant.id) {
          variantIdInput.value = variant.id;
          variantIdInput.disabled = false;
        } else {
          variantIdInput.value = '';
          variantIdInput.disabled = true;
        }
      }
    }

    async updateQuantityRulesCart(html, variant) {
      const container = this.getProductFormContainer();

      if (!variant || !variant.id) {
        const rulesCart = container?.querySelector('.quantity__rules-cart');
        if (rulesCart) {
          rulesCart.classList.add('hidden');
        }
        return;
      }

      const sectionId = this.dataset.section;

      const newQuantityLabel = html.querySelector(`label[for="Quantity-${sectionId}"]`);
      if (newQuantityLabel) {
        const currentQuantityLabel = container?.querySelector(`label[for="Quantity-${sectionId}"]`);
        if (currentQuantityLabel) {
          const newRulesCart = newQuantityLabel.querySelector('.quantity__rules-cart');
          const currentRulesCart = currentQuantityLabel.querySelector('.quantity__rules-cart');
          
          if (newRulesCart && currentRulesCart) {
            currentRulesCart.innerHTML = newRulesCart.innerHTML;
            currentRulesCart.classList.toggle('hidden', newRulesCart.classList.contains('hidden'));
            return;
          }
        }
      }

      try {
        const productUrl = this.dataset.productUrl || window.location.pathname;
        const response = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`);
        const responseText = await response.text();
        const newHtml = new DOMParser().parseFromString(responseText, 'text/html');
        
        const updatedQuantityLabel = newHtml.querySelector(`label[for="Quantity-${sectionId}"]`);
        if (updatedQuantityLabel) {
          const currentQuantityLabel = container?.querySelector(`label[for="Quantity-${sectionId}"]`);
          if (currentQuantityLabel) {
            const updatedRulesCart = updatedQuantityLabel.querySelector('.quantity__rules-cart');
            const currentRulesCart = currentQuantityLabel.querySelector('.quantity__rules-cart');
            
            if (updatedRulesCart && currentRulesCart) {
              currentRulesCart.innerHTML = updatedRulesCart.innerHTML;
              currentRulesCart.classList.toggle('hidden', updatedRulesCart.classList.contains('hidden'));
            }
          }
        }
      } catch (error) {
        console.error('[Variant Picker] Error updating quantity rules cart:', error);
      }
    }


    setUnavailable() {
      const sectionId = this.dataset.section;
      const elements = ['price', 'price-info', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume', 'Volume-Note'];

      elements.forEach(id => {
        const element = document.getElementById(`${id}-${sectionId}`);
        element?.classList.add('hidden');
      });

      const container = this.getProductFormContainer();
      const currentForm = this.getProductForm(container);
      const addToCartButton = currentForm?.querySelector('.product-form__submit')
        || container?.querySelector('.product-form__submit');
      if (addToCartButton) {
        addToCartButton.disabled = true;
        addToCartButton.setAttribute('aria-disabled', 'true');
        const textSpan = addToCartButton.querySelector('.button__text');
        if (textSpan) textSpan.textContent = window.variantStrings?.unavailable || 'Unavailable';
      }

      this.publishEvent('variant:unavailable', {
        sectionId: sectionId
      });
    }

    getProductFormContainer() {
      return this.closest('product-info')
        || this.closest('quick-add-modal')
        || this.closest('section')
        || document;
    }

    getProductForm(container) {
      if (!container) return null;
      return container.querySelector('form[data-type="add-to-cart-form"]')
        || container.querySelector('form[action*="/cart/add"]')
        || container.querySelector('.product-form form');
    }

    setAvailable() {
      const sectionId = this.dataset.section;
      const elements = ['price', 'price-info', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume', 'Volume-Note'];
      
      elements.forEach(id => {
        const element = document.getElementById(`${id}-${sectionId}`);
        if (element) {
          element.classList.remove('hidden');
        }
      });
    }

    updateURL(productUrl, variant) {
      if (this.dataset.updateUrl === 'false') return;
      if (this.closest('quick-add-modal')) return;

      const variantId = variant?.id;
      const url = `${productUrl}${variantId ? `?variant=${variantId}` : ''}`;
      
      window.history.replaceState({}, '', url);

      const shareButton = document.querySelector('share-button');
      if (shareButton && shareButton.updateUrl) {
        shareButton.updateUrl(`${window.shopUrl}${url}`);
      }
    }

    getSelectedVariant(html) {
      const variantScript = html.querySelector('variant-picker [data-selected-variant]');
      if (!variantScript?.textContent) return null;
      
      try {
        return JSON.parse(variantScript.textContent);
      } catch (e) {
        console.error('Failed to parse variant data:', e);
        return null;
      }
    }

    buildRequestUrl(productUrl, selectedOptionValues) {
      const params = [];
      const sectionId = this.dataset.section;
      
      if (sectionId) {
        params.push(`section_id=${sectionId}`);
      }
      
      if (selectedOptionValues.length) {
        params.push(`option_values=${selectedOptionValues.join(',')}`);
      }
      
      return `${productUrl}?${params.join('&')}`;
    }

    getSelectedOptionValues() {
      const selectedOptions = Array.from(
        this.querySelectorAll('select option[selected], fieldset input:checked')
      );
      
      return selectedOptions
        .map(option => option.dataset.optionValueId)
        .filter(Boolean);
    }

    getInputForEventTarget(target) {
      if (!target) return null;
      
      if (target.tagName === 'SELECT') {
        return target.selectedOptions[0];
      }
      
      if (target.tagName === 'LABEL') {
        const inputId = target.getAttribute('for');
        if (inputId) {
          const input = document.getElementById(inputId);
          if (input && (input.type === 'radio' || input.type === 'checkbox')) {
            return input;
          }
        }
        const input = target.querySelector('input[type="radio"], input[type="checkbox"]');
        if (input) return input;
      }
      
      return target;
    }

    publishVariantChange(data) {
      this.publishEvent('variant:change', data);
    }

    publishVariantSelected(variant, html) {
      this.publishEvent('variant:selected', {
        variant: variant,
        sectionId: this.dataset.section,
        html: html,
        featuredMediaId: variant?.featured_media?.id
      });
    }

    onSizeGuideClick(event) {
      const sizeGuideBtn = event.target.closest('.variant-picker__size-guide-btn');
      if (!sizeGuideBtn) {
        return;
      }
      
      event.preventDefault();
      event.stopPropagation();

      const popupType = sizeGuideBtn.dataset.sizePopupType;
      const contentType = sizeGuideBtn.dataset.sizeChartContentType;
      const pageId = sizeGuideBtn.dataset.sizeChartPage;
      const imageId = sizeGuideBtn.dataset.sizeChartImage;
      const sizeChartTitle = sizeGuideBtn.dataset.sizeChartTitle;
      const sizeSelectTitle = sizeGuideBtn.dataset.sizeSelectTitle;
      const sizeOptionName = sizeGuideBtn.dataset.sizeOptionName;
      const sizeConversionEnabled = sizeGuideBtn.dataset.sizeConversionEnabled;
      const sizeConversionType = sizeGuideBtn.dataset.sizeConversionType;
      const sizeConversionRules = sizeGuideBtn.dataset.sizeConversionRules;


      this.publishEvent('size-guide:open', {
        popupType,
        contentType,
        pageId,
        imageId,
        sizeChartTitle,
        sizeSelectTitle,
        sizeOptionName,
        sizeConversionEnabled,
        sizeConversionType,
        sizeConversionRules,
        button: sizeGuideBtn
      });
    }

    publishEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        bubbles: true,
        cancelable: true,
        detail: detail
      });
      
      this.dispatchEvent(event);
      document.dispatchEvent(event);
    }
  });
}